import * as React from 'react';

/** Maps a path segment value (e.g. teamId, decoded author email) to a display label. */
export type BreadcrumbSegmentOverrides = Record<string, string>;

export interface BreadcrumbOverrideActions {
  setSegmentOverride: (segmentValue: string, label: string) => void;
  clearSegmentOverride: (segmentValue: string) => void;
}

/**
 * Two contexts on purpose. Detail pages register a label in an effect that
 * lists the context value in its deps; if that value changed identity every
 * time the map changed, each registration would re-run the effect (clear →
 * set → new map → new value → …) and the page would re-render forever. The
 * actions object below never changes identity, so pages can safely depend on
 * it; only `Breadcrumbs` subscribes to the map itself.
 */
const BreadcrumbOverrideActionsContext = React.createContext<BreadcrumbOverrideActions | null>(
  null
);
const BreadcrumbSegmentOverridesContext = React.createContext<BreadcrumbSegmentOverrides>({});

export function BreadcrumbOverrideProvider({ children }: { children: React.ReactNode }) {
  const [segmentOverrides, setOverrides] = React.useState<BreadcrumbSegmentOverrides>({});

  const actions = React.useMemo<BreadcrumbOverrideActions>(
    () => ({
      setSegmentOverride: (segmentValue, label) => {
        setOverrides((prev) =>
          prev[segmentValue] === label ? prev : { ...prev, [segmentValue]: label }
        );
      },
      clearSegmentOverride: (segmentValue) => {
        setOverrides((prev) => {
          if (!(segmentValue in prev)) return prev;
          const next = { ...prev };
          delete next[segmentValue];
          return next;
        });
      },
    }),
    []
  );

  return (
    <BreadcrumbOverrideActionsContext.Provider value={actions}>
      <BreadcrumbSegmentOverridesContext.Provider value={segmentOverrides}>
        {children}
      </BreadcrumbSegmentOverridesContext.Provider>
    </BreadcrumbOverrideActionsContext.Provider>
  );
}

/** Stable actions for pages that register a friendly label for a URL segment. */
export function useBreadcrumbOverride(): BreadcrumbOverrideActions | null {
  return React.useContext(BreadcrumbOverrideActionsContext);
}

/** The current label map — re-renders on every change; for the breadcrumb bar only. */
export function useBreadcrumbSegmentOverrides(): BreadcrumbSegmentOverrides {
  return React.useContext(BreadcrumbSegmentOverridesContext);
}
